import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const aiHeroImage = '/Ai.png';
const aiSecondaryImage = '/ai_holographic_chip.png';
const aiFallbackImage = '/services_detail_tech_1773741342619.png';

const capabilities = [
  {
    title: 'Predictive Intelligence & Analytics',
    desc: 'Deploy advanced statistical models and neural networks to forecast market demand, risk variables, and operational performance patterns with high-fidelity precision.'
  },
  {
    title: 'Cognitive Workflow Automation',
    desc: 'Orchestrate intelligent automation layers that move beyond simple rules-based tasks to handle complex, unstructured data and decision-making logic.'
  },
  {
    title: 'MLOps & Enterprise Governance',
    desc: 'Establish rigorous deployment pipelines (CI/CD for ML) with integrated model monitoring, drift detection, and auditable governance frameworks.'
  },
  {
    title: 'Generative AI for Enterprise',
    desc: 'Implement secure, private Large Language Models (LLMs) and RAG (Retrieval-Augmented Generation) architectures to unlock value from internal knowledge bases.'
  },
  {
    title: 'Computer Vision & Sensing',
    desc: 'Extract actionable insights from visual data through custom-trained object detection, classification, and spatial analysis models tailored to industrial or retail needs.'
  },
  {
    title: 'Natural Language Processing',
    desc: 'Automate high-volume document analysis, sentiment tracking, and conversational interfaces using sophisticated transformer-based linguistic modeling.'
  }
];



const useCases = [
  { title: 'Dynamic Demand Planning', desc: 'Real-time inventory and supply chain optimization based on external market signals and historical trends.' },
  { title: 'Fraud & Anomaly Detection', desc: 'Real-time financial transaction monitoring to identify and prevent sophisticated fraudulent patterns before they occur.' },
  { title: 'Enterprise Knowledge Search', desc: 'AI-powered internal search tools that aggregate and synthesize insights from thousands of legacy documents.' },
  { title: 'Strategic Customer Insights', desc: 'Hyper-segmentation and churn prediction models that enable proactive engagement and lifetime value optimization.' }
];

const serviceModules = [
  {
    title: 'AI Maturity & Roadmap',
    points: ['Enterprise readiness audit', 'Data infrastructure assessment', 'Use-case ROI prioritization']
  },
  {
    title: 'Data & Model Engineering',
    points: ['ETL & feature pipelines', 'Hyperparameter optimization', 'Distributed model training']
  },
  {
    title: 'Secure GenAI Architecture',
    points: ['Private LLM deployment', 'Vector database integration', 'Prompt engineering & RLHF']
  },
  {
    title: 'Governance & MLOps',
    points: ['Model drift monitoring', 'Bias & fairness auditing', 'Automated retraining loops']
  }
];

const executionSteps = [
  { phase: 'Discovery & Audit', desc: 'Identify high-impact use cases aligned to business goals and audit existing data quality/infrastructure readiness.' },
  { phase: 'Data Strategy', desc: 'Establish robust data pipelines, feature stores, and governance policies to ensure high-fidelity inputs for training.' },
  { phase: 'Model Engineering', desc: 'Develop, evaluate, and fine-tune custom models or adapt enterprise-grade LLMs using rigorous cross-validation.' },
  { phase: 'Validation & Pilot', desc: 'Deploy within controlled production "shadow" environments to measure performance against baseline business KPIs.' },
  { phase: 'Scale & Integration', desc: 'Full-scale rollout across the enterprise with API-led integration into existing CRM, ERP, and operational platforms.' },
  { phase: 'Managed Governance', desc: 'Ongoing monitoring for model performance, drift, and security, supported by 24/7 technical and functional MLOps.' }
];

const techStack = [
  { title: 'Deep Learning', items: ['PyTorch', 'TensorFlow', 'Keras', 'JAX'] },
  { title: 'Data & Processing', items: ['Apache Spark', 'Snowflake', 'DVC', 'MLflow'] },
  { title: 'Generative AI', items: ['OpenAI', 'LangChain', 'Pinecone', 'Hugging Face'] },
  { title: 'Cloud Platforms', items: ['Azure ML', 'AWS SageMaker', 'Vertex AI', 'Databricks'] }
];

const industries = ['Banking & Finance', 'Insurance', 'Healthcare', 'Retail', 'Telecom', 'Energy & Utilities', 'Manufacturing'];

const faqs = [
  {
    q: 'How does Libsys ensure AI model transparency and avoid "Black Box" outcomes?',
    a: 'We prioritize Explainable AI (XAI) as a core tenet of our delivery. For high-stakes enterprise decisions, we implement techniques like SHAP (SHapley Additive exPlanations) and LIME to provide clear visibility into which features are driving a model\'s output. This allows business stakeholders to audit decision logic, identify potential biases, and maintain a "Human-in-the-Loop" governance model that ensures accountability and trust in the AI\'s recommendations.'
  },
  {
    q: 'How do you address data privacy and security when implementing Large Language Models (LLMs)?',
    a: 'Our GenAI strategy is built on privacy-first architecture. We specialize in deploying "Private Tenant" models where your proprietary data never leaves your corporate security perimeter. We leverage Retrieval-Augmented Generation (RAG) so that the LLM references your data locally without ever using it for foundational retraining on public clouds. We also implement robust data masking and PII (Personally Identifiable Information) filters at the prompt-layer to ensure compliance with GDPR, HIPAA, and other regulatory frameworks.'
  },
  {
    q: 'What is your strategy for managing "Model Drift" in production?',
    a: 'We view model deployment as the beginning, not the end, of the AI lifecycle. Our MLOps framework includes real-time telemetry that monitors for "Data Drift" (changes in incoming data distributions) and "Concept Drift" (changes in the relationship between inputs and outputs). When performance drops below a pre-validated threshold, our automated pipelines trigger retraining alerts, allowing data scientists to validate and redeploy updated models before the business experiences a material impact on decision quality.'
  },
  {
    q: 'How long does it typically take to realize measurable ROI from an AI initiative?',
    a: 'We aim for "Early Value realization" through a 6-8 week Proof-of-Value (PoV) phase. This phase is designed to prove the technical feasibility and business impact of a specific use case before a full-scale investment is made. Once validated, a production-grade rollout typically takes 3-5 months, with business impact being tracked against baseline KPIs from day one. Our goal is to ensure the AI program pays for itself within the first 12 months through measured operational gains.'
  },
  {
    q: 'How does Libsys help organizations manage the cost of scaling AI infrastructure?',
    a: 'Scaling AI can be expensive if not managed correctly. We focus on "Optimized Inference" strategies, where we select the right model size for each specific task (using smaller, specialized models instead of generic, massive LLMs where possible). We also implement cloud-governance tools to manage compute spending, leveraging spot-instances for non-critical training and serverless architectures for inference to ensure that infrastructure costs scale predictably and efficiently with your business volume.'
  },
  {
    q: 'What role does data quality play in your delivery model?',
    a: 'Data quality is the single most important factor in AI success. Our methodology includes a mandatory "Data Readiness Audit" before any model engineering begins. We help organizations identify and close gaps in their data collection, cleaning, and labeling processes. If the data isn\'t ready, we provide the engineering support to fix it, because building an AI model on poor-quality data leads to unreliable outcomes and wasted investment.'
  }
];

const ServiceAI = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="AI & Machine Learning Services"
        description="Libsys AI and machine learning consulting — predictive analytics, computer vision, and AI assistants built for production. Model engineering, MLOps, and deployment governance."
        path="/services/ai"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
              Intelligence & Automation
            </span>
            <h1 className="text-h1 text-primary mb-5">
              Enterprise AI: Transforming Data into Predictive Power
            </h1>
            <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
              Libsys helps organizations move from isolated AI experiments to production-grade ecosystems that deliver measurable business impact. We build secure, governed, and scalable AI solutions that move the needle on planning accuracy, operational cost, and customer engagement. Our approach combines rigorous data engineering with the latest in Deep Learning and Generative AI to bridge the gap between technical potential and executive ROI. By integrating comprehensive governance frameworks and continuous monitoring, we ensure that AI remains a transparent and reliable asset for enterprise-wide decision making.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
              {[
                'Predictive Analytics for Strategic Planning',
                'Secure Enterprise GenAI Architecture',
                'Governed MLOps & Delivery Framework',
                'Cognitive Workflow & Decision Automation',
                'Advanced Data & Feature Engineering',
                'Continuous Performance & Drift Monitoring'
              ].map((item) => (
                <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+16307991556"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
              >
                Request AI Strategy Session
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 lg:-mt-12"
          >
            <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
              <img
                src={aiHeroImage}
                alt="AI Intelligence Simulation"
                className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                onError={(e) => {
                  if (!e.currentTarget.dataset.triedPublic) {
                    e.currentTarget.dataset.triedPublic = '1';
                    e.currentTarget.src = '/Ai.png';
                    return;
                  }
                  e.currentTarget.src = aiFallbackImage;
                }}
              />
            </div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Depth</span>
            <h2 className="text-h2 text-primary">Advanced Capabilities Across the AI Ecosystem</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              Our AI practice integrates high-precision engineering with strategic business context, ensuring that models perform reliably in high-stakes enterprise environments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Portfolio</span>
              <h2 className="text-h2 text-primary mb-6">Strategic AI Service Modules</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                We provide end-to-end AI services with clear roadmap ownership so programs can scale from initial proof-of-value to unified enterprise deployment without losing governance or business alignment.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {serviceModules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={aiSecondaryImage} alt="AI Delivery Pipeline" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = aiFallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Bridging the gap between isolated data experiments and scaled enterprise value."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">Governance Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Disciplined Path to AI Production</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 Success in AI requires more than just algorithms; it requires a structured lifecycle that manages data quality, technical risk, and organizational adoption in lockstep.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK & USE CASES ════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Technology Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">Modern Toolsets for Professional Grade AI</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                We utilize a specialized, hybrid tech-stack that connects proven cloud environments with the latest open-source innovation to ensure your AI models are performant, portable, and secure. Our selection criteria prioritize security, horizontal scalability, and ease of governance.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {techStack.map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                   <h3 className="text-xl font-black text-primary mb-6">Strategic Use-Cases</h3>
                   <div className="space-y-6">
                      {useCases.map(uc => (
                          <div key={uc.title} className="border-b border-slate-100 pb-6 last:border-b-0 last:pb-0">
                             <h4 className="text-primary font-black text-subtext uppercase tracking-wide mb-2">{uc.title}</h4>
                             <p className="text-slate-600 text-subtext font-medium leading-relaxed">{uc.desc}</p>
                          </div>
                      ))}
                   </div>
                </div>
             </div>

             <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                   <h3 className="text-xl font-black text-primary mb-6">Enterprise Impact Benchmarks</h3>
                   <div className="space-y-4">
                      {[
                        'Automated reconciliation in global finance workflows.',
                        'Visual quality monitoring in discrete manufacturing.',
                        'Intelligent churn prediction in high-volume retail.',
                        'Secure knowledge retrieval for large legal datasets.'
                      ].map(b => (
                         <div key={b} className="flex items-start gap-4">
                            <div className="w-2 h-2 rounded-full bg-secondary mt-2.5 flex-shrink-0" />
                            <p className="text-slate-600 text-body font-medium leading-relaxed">{b}</p>
                         </div>
                      ))}
                   </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8">
                   <h3 className="text-[17px] font-black text-primary mb-4">Target Industries</h3>
                   <div className="flex flex-wrap gap-2">
                     {industries.map(ind => (
                        <span key={ind} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-subtext font-bold text-slate-600 shadow-sm">{ind}</span>
                     ))}
                   </div>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceAI;
